//----------------------------------------------------------------------------------------------------------------------
// State Machine Behavior
//----------------------------------------------------------------------------------------------------------------------

import { GameEntityBehavior } from '../classes/entity.ts';
import type { GameEvent } from '../classes/eventBus.ts';
import { StateMachine } from '../utils/stateMachine.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * A single transition definition for the state machine behavior configuration.
 */
export interface TransitionConfig<States extends string>
{
    from : States;
    to : States;
    guard ?: () => boolean;
}

/**
 * A wildcard transition definition (from any state).
 */
export interface WildcardTransitionConfig<States extends string>
{
    to : States;
    guard ?: () => boolean;
}

/**
 * Configuration for creating a StateMachineBehavior subclass.
 */
export interface StateMachineBehaviorConfig<States extends string, EntityState extends object>
{
    /** The initial state of the state machine. */
    initialState : States;

    /** The key in the entity's state object to update when state changes. */
    stateKey : keyof EntityState & string;

    /** Valid transitions between states. */
    transitions : TransitionConfig<States>[];

    /** Wildcard transitions (from any state). */
    wildcardTransitions ?: WildcardTransitionConfig<States>[];
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * A behavior that wraps a {@link StateMachine} and ties it to entity state.
 *
 * Since behaviors are instantiated with `new BehaviorClass()` (no constructor args),
 * use the static factory `StateMachineBehavior.create(config)` to produce a configured subclass.
 *
 * @example
 * ```typescript
 * const EnemyAI = StateMachineBehavior.create<EnemyStates, EnemyState>({
 *     initialState: 'idle',
 *     stateKey: 'aiState',
 *     transitions: [
 *         { from: 'idle', to: 'patrol' },
 *         { from: 'patrol', to: 'chase' },
 *         { from: 'chase', to: 'attack' },
 *     ],
 *     wildcardTransitions: [
 *         { to: 'dead' },
 *     ],
 * });
 * ```
 */
export class StateMachineBehavior<
    States extends string = string,
    EntityState extends object = object,
> extends GameEntityBehavior<EntityState>
{
    name = 'stateMachine';
    eventSubscriptions : string[] = [];

    protected stateMachine : StateMachine<States>;
    protected stateKey : keyof EntityState & string;

    protected constructor()
    {
        super();

        // These will be overridden by the factory-produced subclass constructor.
        // Default to a dummy state machine that will be replaced.
        this.stateMachine = new StateMachine<States>('' as States);
        this.stateKey = '' as keyof EntityState & string;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * The current state of the state machine.
     */
    get currentState() : States
    {
        return this.stateMachine.currentState;
    }

    /**
     * Transition to a new state. Updates the entity's state and emits a state changed event.
     *
     * @throws If the transition is not defined or a guard rejects it.
     */
    transition(to : States) : void
    {
        this.stateMachine.transition(to);

        // Update entity state
        if(this.entity)
        {
            (this.entity.state as Record<string, unknown>)[this.stateKey] = to;
            this.$emitStateChanged();
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Behavior Implementation
    //------------------------------------------------------------------------------------------------------------------

    processEvent(_event : GameEvent, _state : EntityState) : boolean
    {
        return false;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Static Factory
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Creates a configured StateMachineBehavior subclass with the given state machine configuration baked in.
     * The returned class can be used directly as a behavior constructor.
     */
    static create<States extends string, EntityState extends object>(
        config : StateMachineBehaviorConfig<States, EntityState>
    ) : new () => StateMachineBehavior<States, EntityState>
    {
        // Capture config in closure
        const { initialState, stateKey, transitions, wildcardTransitions } = config;

        class ConfiguredStateMachineBehavior extends StateMachineBehavior<States, EntityState>
        {
            constructor()
            {
                super();

                this.stateKey = stateKey;
                this.stateMachine = new StateMachine<States>(initialState);

                for(const tr of transitions)
                {
                    this.stateMachine.addTransition(tr.from, tr.to, tr.guard);
                }

                if(wildcardTransitions)
                {
                    for(const wt of wildcardTransitions)
                    {
                        this.stateMachine.addTransitionFromAny(wt.to, wt.guard);
                    }
                }
            }
        }

        return ConfiguredStateMachineBehavior;
    }
}

//----------------------------------------------------------------------------------------------------------------------
